import { AccountAccess, CompanyUserProductAccount, UserClaim } from '@treasury/domain/company/user';
import { TmBaseComponent } from '@treasury/presentation';
import '@treasury/presentation/components/jh-ui';
import { css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import {
    ClaimGroup,
    getClaimDisplayLabel,
    groupClaimsByBaseName,
} from '../utils/claim-grouping.util';

export type ApproveOrRejectConfirmationData = { comments: string };

export enum Decision {
    Approve = 'approve',
    Reject = 'reject',
}

type FormControlEvent<T> = CustomEvent<{ value: T; isValid: boolean }>;

const tagName = 'tm-company-user-account-permissions-drawer';
@customElement(tagName)
export class CompanyUserAccountPermissionsDrawer extends TmBaseComponent {
    @property({ type: Object })
    public accountInfo?: AccountAccess;

    @property({ type: Object })
    public accountClaims?: CompanyUserProductAccount;

    public render() {
        return html`${this.renderAccountEntitlements()}`;
    }

    private renderAccountEntitlements() {
        const claims = this.accountClaims?.userAccountClaims || [];
        const groupedClaims = groupClaimsByBaseName(claims);
        return html` <div class="text-lg font-semibold mb-3">Entitlements</div>
            <div class="flex flex-col gap-y-10">
                ${groupedClaims.map(group => this.renderClaimGroup(group))}
            </div>`;
    }

    private renderClaimGroup(group: ClaimGroup<UserClaim>) {
        const isAllSelected = group.claims.every(child => child.claim.hasUserClaim);
        return html`<div>
            <tm-wc-checkbox
                class="ext-lg font-semibold mb-3"
                @value-changed=${(e: FormControlEvent<boolean>) => this.handleSelectAll(e, group)}
                .initial=${isAllSelected}
                .label=${group.baseName}
            ></tm-wc-checkbox>
            <div class="flex flex-col gap-2">
                ${group.claims.map(groupedClaim =>
                    this.renderClaim(groupedClaim.claim, groupedClaim.action)
                )}
            </div>
        </div>`;
    }

    private renderClaim(claim: UserClaim, action?: string) {
        const label = getClaimDisplayLabel(claim, action);
        return html`<div class="min-w-0 flex-shrink-0">
            <tm-wc-checkbox
                class="ml-10"
                .label=${label}
                .initial=${claim.hasUserClaim}
                @value-changed=${(e: FormControlEvent<boolean>) => this.handleClaimChange(e, claim)}
            ></tm-wc-checkbox>
        </div>`;
    }

    private handleClaimChange(e: FormControlEvent<boolean>, claim: UserClaim) {
        const { value } = e.detail;
        claim.hasUserClaim = value;
        this.requestUpdate();
    }

    private handleSelectAll(e: FormControlEvent<boolean>, group: ClaimGroup<UserClaim>) {
        const { value } = e.detail;
        group.claims.forEach(child => {
            child.claim.hasUserClaim = value;
        });
        this.requestUpdate();
    }

    public static get styles() {
        return css`
            :host {
                --jh-size-1200: 12rem;
            }
        `;
    }
}
